import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { TakeittoLogo } from '../components/TakeittoLogo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Building2, CreditCard, Link2, CheckCircle, ArrowRight, ArrowLeft,
  Phone, MapPin, Clock, Package, Users, Zap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 'business', label: 'Business', icon: Building2 },
  { id: 'plan', label: 'Plan', icon: CreditCard },
  { id: 'connect', label: 'Connect', icon: Link2 },
  { id: 'done', label: 'Done', icon: CheckCircle }
];

const AUSTRALIAN_STATES = [
  { value: 'NSW', label: 'New South Wales' },
  { value: 'VIC', label: 'Victoria' },
  { value: 'QLD', label: 'Queensland' },
  { value: 'WA', label: 'Western Australia' },
  { value: 'SA', label: 'South Australia' },
  { value: 'TAS', label: 'Tasmania' },
  { value: 'ACT', label: 'Australian Capital Territory' },
  { value: 'NT', label: 'Northern Territory' }
];

const TIMEZONES = [
  { value: 'Australia/Sydney', label: 'Sydney / Melbourne (AEST)' },
  { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
  { value: 'Australia/Perth', label: 'Perth (AWST)' },
  { value: 'Australia/Adelaide', label: 'Adelaide (ACST)' },
  { value: 'Australia/Darwin', label: 'Darwin (ACST)' }
];

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 49,
    features: ['100 orders/month', '3 drivers', 'Basic support', 'API access'],
    popular: false
  },
  {
    id: 'growth',
    name: 'Growth',
    price: 149,
    features: ['500 orders/month', '10 drivers', 'Priority support', 'API access', 'Analytics'],
    popular: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 399,
    features: ['Unlimited orders', 'Unlimited drivers', '24/7 support', 'API access', 'Custom integrations'],
    popular: false
  }
];

export default function MerchantOnboard() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [merchantData, setMerchantData] = useState(null);
  const [apiKey, setApiKey] = useState('');
  
  const [formData, setFormData] = useState({
    business_name: '',
    abn: '',
    phone: '',
    street_address: '',
    city: '',
    state: 'NSW',
    postcode: '',
    timezone: 'Australia/Sydney',
    email: '',
    password: '',
    contact_name: '',
    plan: 'growth'
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateStep = () => {
    if (currentStep === 0) {
      const required = ['business_name', 'phone', 'street_address', 'city', 'postcode', 'email', 'password', 'contact_name'];
      for (const field of required) {
        if (!formData[field]) {
          toast.error(`Please fill in ${field.replace('_', ' ')}`);
          return false;
        }
      }
      if (!formData.email.includes('@')) {
        toast.error('Please enter a valid email');
        return false;
      }
      if (formData.password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return false;
      }
    }
    return true;
  };

  const handleNext = async () => {
    if (!validateStep()) return;
    
    if (currentStep === 0) {
      // Register merchant
      setIsLoading(true);
      try {
        const res = await axios.post(`${API}/merchants/register`, formData);
        setMerchantData(res.data);
        // Update auth context so protected routes recognise the session
        setSession(res.data.token, res.data.user);
        toast.success('Account created successfully!');
        // Fetch the real API key for the Connect step
        try {
          const keyRes = await axios.get(`${API}/merchants/me/api-keys`, {
            headers: { Authorization: `Bearer ${res.data.token}` }
          });
          setApiKey(keyRes.data.api_key || '');
        } catch (e) {
          // Non-blocking: key can still be fetched later from the dashboard
        }
        setCurrentStep(1);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Registration failed');
      } finally {
        setIsLoading(false);
      }
    } else if (currentStep === 1) {
      // Update plan
      setIsLoading(true);
      try {
        await axios.post(`${API}/merchants/me/plan`, { plan: formData.plan }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setCurrentStep(2);
      } catch (error) {
        toast.error('Failed to update plan');
      } finally {
        setIsLoading(false);
      }
    } else if (currentStep === 2) {
      setCurrentStep(3);
    } else if (currentStep === 3) {
      navigate('/merchant');
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const copyApiKey = async () => {
    const value = apiKey || '';
    if (!value) {
      toast.error('API key not available yet');
      return;
    }
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('API key copied!');
    } catch (e) {
      toast.error('Could not copy automatically. Please copy the key manually.');
    }
  };

  const renderProgressBar = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between max-w-2xl mx-auto">
        {STEPS.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === currentStep;
          const isCompleted = index < currentStep;
          
          return (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  isCompleted ? 'bg-emerald-500 text-white' :
                  isActive ? 'bg-indigo-600 text-white' :
                  'bg-gray-200 text-gray-400'
                }`}>
                  {isCompleted ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className={`text-xs mt-2 ${isActive ? 'text-indigo-600 font-semibold' : 'text-gray-500'}`}>
                  {step.label}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`flex-1 h-1 mx-4 rounded ${
                  index < currentStep ? 'bg-emerald-500' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );

  const renderBusinessStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <p className="text-indigo-600 font-medium text-sm mb-2">STEP 1 OF 4</p>
        <h2 className="text-2xl font-bold text-gray-900">Your business details</h2>
        <p className="text-gray-500 mt-1">Tell us about your business so we can set up your account correctly.</p>
      </div>
      
      <div className="space-y-4">
        <div>
          <Label htmlFor="business_name">Business Name *</Label>
          <Input
            id="business_name"
            name="business_name"
            value={formData.business_name}
            onChange={handleInputChange}
            placeholder="Your Business Name"
            className="mt-1"
            data-testid="onboard-business-name"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="abn">ABN</Label>
            <Input
              id="abn"
              name="abn"
              value={formData.abn}
              onChange={handleInputChange}
              placeholder="12 345 678 901"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="phone">Phone *</Label>
            <Input
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="+61 400 000 000"
              className="mt-1"
            />
          </div>
        </div>
        
        <div>
          <Label htmlFor="street_address">Street Address *</Label>
          <Input
            id="street_address"
            name="street_address"
            value={formData.street_address}
            onChange={handleInputChange}
            placeholder="123 Market Street"
            className="mt-1"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="city">City *</Label>
            <Input
              id="city"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              placeholder="Sydney"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="state">State *</Label>
            <Select value={formData.state} onValueChange={(v) => handleSelectChange('state', v)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUSTRALIAN_STATES.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="postcode">Postcode *</Label>
            <Input
              id="postcode"
              name="postcode"
              value={formData.postcode}
              onChange={handleInputChange}
              placeholder="2000"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="timezone">Timezone</Label>
            <Select value={formData.timezone} onValueChange={(v) => handleSelectChange('timezone', v)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="border-t pt-4 mt-6">
          <h3 className="font-semibold text-gray-900 mb-4">Account Details</h3>
          <div className="space-y-4">
            <div>
              <Label htmlFor="contact_name">Your Name *</Label>
              <Input
                id="contact_name"
                name="contact_name"
                value={formData.contact_name}
                onChange={handleInputChange}
                placeholder="John Smith"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="you@business.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Minimum 6 characters"
                className="mt-1"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPlanStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <p className="text-indigo-600 font-medium text-sm mb-2">STEP 2 OF 4</p>
        <h2 className="text-2xl font-bold text-gray-900">Choose your plan</h2>
        <p className="text-gray-500 mt-1">Select a plan that fits your business needs.</p>
      </div>
      
      <div className="grid grid-cols-3 gap-4">
        {PLANS.map(plan => (
          <div
            key={plan.id}
            className={`relative rounded-xl border-2 p-6 cursor-pointer transition-all ${
              formData.plan === plan.id
                ? 'border-indigo-600 bg-indigo-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => handleSelectChange('plan', plan.id)}
          >
            {plan.popular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-xs px-3 py-1 rounded-full">
                Popular
              </div>
            )}
            
            <h3 className="font-semibold text-lg text-gray-900">{plan.name}</h3>
            <div className="mt-2">
              <span className="text-3xl font-bold text-gray-900">${plan.price}</span>
              <span className="text-gray-500">/month</span>
            </div>
            
            <ul className="mt-4 space-y-2">
              {plan.features.map((feature, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  {feature}
                </li>
              ))}
            </ul>
            
            {formData.plan === plan.id && (
              <div className="absolute top-4 right-4">
                <CheckCircle className="w-6 h-6 text-indigo-600" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderConnectStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <p className="text-indigo-600 font-medium text-sm mb-2">STEP 3 OF 4</p>
        <h2 className="text-2xl font-bold text-gray-900">Connect your systems</h2>
        <p className="text-gray-500 mt-1">Use these API credentials to integrate with your order management system.</p>
      </div>
      
      <div className="bg-gray-50 rounded-xl p-6 space-y-4">
        <div>
          <Label className="text-gray-500">API Key</Label>
          <div className="flex items-center gap-2 mt-1">
            <Input
              value={apiKey || 'Loading...'}
              readOnly
              className="font-mono bg-white"
              data-testid="onboard-api-key"
            />
            <Button
              variant="outline"
              onClick={copyApiKey}
              data-testid="onboard-copy-api-key"
            >
              Copy
            </Button>
          </div>
        </div>
        
        <div className="border-t pt-4">
          <h4 className="font-semibold text-gray-900 mb-2">API Endpoint</h4>
          <code className="block bg-gray-900 text-emerald-400 p-4 rounded-lg text-sm">
            POST {process.env.REACT_APP_BACKEND_URL}/api/webhook/orders
          </code>
        </div>
        
        <div className="border-t pt-4">
          <h4 className="font-semibold text-gray-900 mb-2">Example Request</h4>
          <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
{`curl -X POST "${process.env.REACT_APP_BACKEND_URL}/api/webhook/orders" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "customer_name": "John Doe",
    "delivery_address": "123 George St, Sydney NSW 2000",
    "order_value": 85.50,
    "total_weight": 2.5,
    "delivery_time_window": "2:00 PM - 4:00 PM"
  }'`}
          </pre>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> You can access your full API keys anytime from the Merchant Dashboard settings.
          </p>
        </div>
      </div>
    </div>
  );

  const renderDoneStep = () => (
    <div className="text-center space-y-6">
      <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
        <CheckCircle className="w-10 h-10 text-emerald-600" />
      </div>
      
      <div>
        <h2 className="text-2xl font-bold text-gray-900">You're all set!</h2>
        <p className="text-gray-500 mt-2">Your account is ready. Start managing your deliveries now.</p>
      </div>
      
      <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
        <div className="bg-gray-50 rounded-lg p-4">
          <Package className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
          <p className="text-sm text-gray-600">Create Orders</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <Users className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
          <p className="text-sm text-gray-600">Track Drivers</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <Zap className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
          <p className="text-sm text-gray-600">Auto-Assign</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50" data-testid="merchant-onboard">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <TakeittoLogo size="sm" />
          <p className="text-sm text-gray-500">Ready in 5 minutes</p>
        </div>
      </header>
      
      <main className="max-w-2xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Set up your account</h1>
        </div>
        
        {renderProgressBar()}
        
        <div className="bg-white rounded-2xl shadow-lg p-8">
          {currentStep === 0 && renderBusinessStep()}
          {currentStep === 1 && renderPlanStep()}
          {currentStep === 2 && renderConnectStep()}
          {currentStep === 3 && renderDoneStep()}
          
          <div className="flex items-center justify-between mt-8 pt-6 border-t">
            {currentStep > 0 && currentStep < 3 ? (
              <Button variant="ghost" onClick={handleBack}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            ) : (
              <div />
            )}
            
            <Button 
              onClick={handleNext} 
              disabled={isLoading}
              className="bg-amber-500 hover:bg-amber-600 text-white px-8"
              data-testid="onboard-continue-btn"
            >
              {isLoading ? 'Processing...' : currentStep === 3 ? 'Go to Dashboard' : 'Continue'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
        
        {currentStep === 0 && (
          <p className="text-center text-sm text-gray-500 mt-6">
            Already have an account?{' '}
            <a href="/login" className="text-indigo-600 hover:underline">Sign in</a>
          </p>
        )}
      </main>
    </div>
  );
}
